import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuthContext } from "@/contexts/AuthContext";
import { RealtimeChannel } from "@supabase/supabase-js";

// Scene types
export type SceneType = "beach" | "cafe" | "rooftop" | "night_city";

export const sceneConfig: Record<SceneType, { emoji: string; label: string; gradient: string }> = {
  beach: { emoji: "🏖️", label: "Beach", gradient: "from-cyan-400 to-blue-500" },
  cafe: { emoji: "☕", label: "Café", gradient: "from-amber-400 to-orange-500" },
  rooftop: { emoji: "🌃", label: "Rooftop", gradient: "from-purple-500 to-pink-500" },
  night_city: { emoji: "🌙", label: "Night City", gradient: "from-indigo-600 to-violet-700" },
};

// Presence state
export type PresenceState = "viewing" | "live" | "offline";

export interface SceneUser {
  id: string;
  name: string;
  photo_url: string | null;
  has_video: boolean;
  is_live: boolean;
  online_at: string;
}

export interface ScenePresence {
  users: SceneUser[];
  liveCount: number;
  viewingCount: number;
  totalCount: number;
}

export interface CountryLiveStats {
  liveCount: number;
  onVideoCount: number;
  trendingScene: SceneType;
  topCities: Array<{ id: string; name: string; liveCount: number }>;
}

// Hook to get live stats for countries
export const useCountryLiveStats = (countryCodes: string[]) => {
  const [stats, setStats] = useState<Record<string, CountryLiveStats>>({});
  const channelsRef = useRef<Map<string, RealtimeChannel>>(new Map());

  useEffect(() => {
    if (countryCodes.length === 0) return;

    const setupChannels = async () => {
      for (const code of countryCodes) {
        if (channelsRef.current.has(code)) continue;

        const channelName = `scene:country:${code}`;
        const channel = supabase.channel(channelName);

        channel
          .on("presence", { event: "sync" }, () => {
            const state = channel.presenceState();
            let liveCount = 0;
            let onVideoCount = 0;
            const sceneCounts: Record<SceneType, number> = { beach: 0, cafe: 0, rooftop: 0, night_city: 0 };
            const cityCounts: Record<string, { id: string; name: string; count: number }> = {};

            Object.values(state).forEach((presences: any[]) => {
              presences.forEach((p) => {
                liveCount++;
                if (p.is_live) onVideoCount++;
                if (p.scene) sceneCounts[p.scene as SceneType] = (sceneCounts[p.scene as SceneType] || 0) + 1;
                if (p.city_id && p.city_name) {
                  if (!cityCounts[p.city_id]) {
                    cityCounts[p.city_id] = { id: p.city_id, name: p.city_name, count: 0 };
                  }
                  cityCounts[p.city_id].count++;
                }
              });
            });

            // Find trending scene
            const trendingScene = (Object.entries(sceneCounts)
              .sort((a, b) => b[1] - a[1])[0]?.[0] || "beach") as SceneType;

            // Sort cities by count
            const topCities = Object.values(cityCounts)
              .sort((a, b) => b.count - a.count)
              .slice(0, 3)
              .map((c) => ({ id: c.id, name: c.name, liveCount: c.count }));

            setStats((prev) => ({
              ...prev,
              [code]: { liveCount, onVideoCount, trendingScene, topCities },
            }));
          })
          .subscribe();

        channelsRef.current.set(code, channel);
      }
    };

    setupChannels();

    return () => {
      channelsRef.current.forEach((channel) => {
        supabase.removeChannel(channel);
      });
      channelsRef.current.clear();
    };
  }, [countryCodes]);

  return stats;
};

// Hook to get scene presence for a specific city/scene
export const useScenePresence = (
  countryCode: string | null,
  cityId: string | null,
  scene: SceneType | null
) => {
  const { user, profile } = useAuthContext();
  const [presence, setPresence] = useState<ScenePresence>({
    users: [],
    liveCount: 0,
    viewingCount: 0,
    totalCount: 0,
  });
  const [isInScene, setIsInScene] = useState(false);
  const [isLive, setIsLive] = useState(false);
  const channelRef = useRef<RealtimeChannel | null>(null);
  const countryChannelRef = useRef<RealtimeChannel | null>(null);

  // Join scene
  const joinScene = useCallback(
    async (goLive = false) => {
      if (!countryCode || !cityId || !scene || !user || !profile) return;

      const channelName = `scene:${countryCode}:${cityId}:${scene}`;

      // Clean up existing channels
      if (channelRef.current) {
        await supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      if (countryChannelRef.current) {
        await supabase.removeChannel(countryChannelRef.current);
        countryChannelRef.current = null;
      }

      const channel = supabase.channel(channelName);

      channel
        .on("presence", { event: "sync" }, () => {
          const state = channel.presenceState();
          const users: SceneUser[] = [];

          Object.values(state).forEach((presences: any[]) => {
            presences.forEach((p) => {
              users.push({
                id: p.id,
                name: p.name,
                photo_url: p.photo_url,
                has_video: p.has_video || false,
                is_live: p.is_live || false,
                online_at: p.online_at,
              });
            });
          });

          const liveUsers = users.filter((u) => u.is_live);
          const viewingUsers = users.filter((u) => !u.is_live);

          setPresence({
            users,
            liveCount: liveUsers.length,
            viewingCount: viewingUsers.length,
            totalCount: users.length,
          });
        })
        .subscribe(async (status) => {
          if (status === "SUBSCRIBED") {
            try {
              await channel.track({
                id: user.id,
                name: profile.name || "Anonymous",
                photo_url: profile.photo_url,
                has_video: profile.has_intro_video || false,
                is_live: goLive,
                online_at: new Date().toISOString(),
              });
              setIsInScene(true);
              setIsLive(goLive);
            } catch (error) {
              console.error("Error tracking presence:", error);
            }
          }
        });

      channelRef.current = channel;

      // Also track in country-level channel for aggregated stats
      const countryChannel = supabase.channel(`scene:country:${countryCode}:${user.id}`);
      countryChannelRef.current = countryChannel;
      
      countryChannel.subscribe(async (status) => {
        if (status === "SUBSCRIBED") {
          try {
            await countryChannel.track({
              id: user.id,
              name: profile.name || "Anonymous",
              photo_url: profile.photo_url,
              is_live: goLive,
              scene,
              city_id: cityId,
              city_name: "",
              online_at: new Date().toISOString(),
            });
          } catch (error) {
            console.error("Error tracking country presence:", error);
          }
        }
      });
    },
    [countryCode, cityId, scene, user, profile]
  );

  // Go live (toggle video)
  const goLive = useCallback(async () => {
    if (!channelRef.current || !user || !profile) return;

    try {
      await channelRef.current.track({
        id: user.id,
        name: profile.name || "Anonymous",
        photo_url: profile.photo_url,
        has_video: profile.has_intro_video || false,
        is_live: true,
        online_at: new Date().toISOString(),
      });
      setIsLive(true);
    } catch (error) {
      console.error("Error going live:", error);
    }
  }, [user, profile]);

  // Stop live
  const stopLive = useCallback(async () => {
    if (!channelRef.current || !user || !profile) return;

    try {
      await channelRef.current.track({
        id: user.id,
        name: profile.name || "Anonymous",
        photo_url: profile.photo_url,
        has_video: profile.has_intro_video || false,
        is_live: false,
        online_at: new Date().toISOString(),
      });
      setIsLive(false);
    } catch (error) {
      console.error("Error stopping live:", error);
    }
  }, [user, profile]);

  // Leave scene
  const leaveScene = useCallback(async () => {
    try {
      if (channelRef.current) {
        await channelRef.current.untrack();
        await supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
      if (countryChannelRef.current) {
        await countryChannelRef.current.untrack();
        await supabase.removeChannel(countryChannelRef.current);
        countryChannelRef.current = null;
      }
    } catch (error) {
      console.error("Error leaving scene:", error);
    }
    setIsInScene(false);
    setIsLive(false);
    setPresence({ users: [], liveCount: 0, viewingCount: 0, totalCount: 0 });
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
      }
      if (countryChannelRef.current) {
        supabase.removeChannel(countryChannelRef.current);
      }
    };
  }, []);

  return {
    presence,
    isInScene,
    isLive,
    joinScene,
    goLive,
    stopLive,
    leaveScene,
  };
};

// Get fallback users (recently active) for Vibe Mode
export const useVibeModeUsers = (countryCode: string, cityId: string) => {
  const [users, setUsers] = useState<SceneUser[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchRecentUsers = async () => {
      const { data } = await supabase
        .from("profiles")
        .select("id, name, photo_url, has_intro_video")
        .order("updated_at", { ascending: false })
        .limit(8);

      if (data) {
        setUsers(
          data.map((u: any) => ({
            id: u.id,
            name: u.name,
            photo_url: u.photo_url,
            has_video: u.has_intro_video || false,
            is_live: false,
            online_at: "",
          }))
        );
      }
      setLoading(false);
    };

    fetchRecentUsers();
  }, [countryCode, cityId]);

  return { users, loading };
};
